import { DateAdapter } from "@/infrastructure/adapters/dayJs/DayJs.adapter";
import { DateService, NumberFormatterService } from "@/core/services";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import { NumberFormatType } from "@/core/types";
import { AccountingPurchaser, AccountingPurchaserProps } from "./AccountingPurchaser";
import { AccountingDeductionGroup, AccountingDeductionGroupProps } from "./AccountingDeductionGroup";

export interface AccountingRemittanceProps {
  remittanceId: number;
  checkNumber: string;
  checkDate: string;
  checkAmount: number;
  cmTotal: number;
  deductionsCount: number;
  purchaser: AccountingPurchaserProps;
  accountingDeductions: AccountingDeductionGroupProps[];
}

export class AccountingRemittance {
  private readonly _remittanceId: number;
  private readonly _checkNumber: string;
  private readonly _checkDate: string;
  private readonly _checkAmount: number;
  private readonly _cmTotal: number;
  private readonly _deductionsCount: number;
  private readonly _purchaser: AccountingPurchaser;
  private readonly _accountingDeductions: AccountingDeductionGroup[];

  constructor(props: AccountingRemittanceProps) {
    this._remittanceId = props.remittanceId;
    this._checkNumber = props.checkNumber;
    this._checkDate = props.checkDate;
    this._checkAmount = Number(props.checkAmount);
    this._cmTotal = Number(props.cmTotal);
    this._deductionsCount = props.deductionsCount;
    this._purchaser = AccountingPurchaser.fromJson(props.purchaser);
    this._accountingDeductions = (props.accountingDeductions ?? []).map(group =>
      AccountingDeductionGroup.fromJson(group)
    );
  }

  public get key(): number {
    return this._remittanceId;
  }

  public get remittanceId(): number {
    return this._remittanceId;
  }

  public get checkNumber(): string {
    return this._checkNumber;
  }

  public get checkDate(): string {
    return this._checkDate;
  }

  public checkDateFormatted(format: string): string {
    if (!this._checkDate) return "-";
    return new DateService(new DateAdapter()).format(this._checkDate, format);
  }

  public get checkAmount(): number {
    return this._checkAmount;
  }

  public get cmTotal(): number {
    return this._cmTotal;
  }

  public cmTotalFormatted(format: NumberFormatType): string {
    return new NumberFormatterService(new NumberFormatterAdapter()).format(this._cmTotal, format);
  }

  public get deductionsCount(): number {
    return this._deductionsCount;
  }

  public get purchaser(): AccountingPurchaser {
    return this._purchaser;
  }

  public get accountingDeductions(): AccountingDeductionGroup[] {
    return this._accountingDeductions;
  }

  public static fromJson(props: AccountingRemittanceProps): AccountingRemittance {
    return new AccountingRemittance(props);
  }
}
